import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Building2, Users as UsersIcon } from "lucide-react";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const empty = { name_en: "", name_ar: "", description: "", manager_user_id: "", color: "#7A1F3D", active: true };

export default function Departments() {
  const { lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [d, u, e] = await Promise.all([
      api.get("/departments"),
      api.get("/users"),
      api.get("/employees"),
    ]);
    setItems(d.data); setUsers(u.data); setEmployees(e.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name_en || !form.name_ar) return toast.error(lang === "ar" ? "الاسم مطلوب" : "Name required");
    try {
      if (editId) await api.put(`/departments/${editId}`, form);
      else await api.post("/departments", form);
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف القسم؟" : "Delete department?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/departments/${id}`);
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const edit = (d) => {
    setForm({ ...d, manager_user_id: d.manager_user_id || "" });
    setEditId(d.department_id);
    setOpen(true);
  };

  const empCountBy = (did) => employees.filter((e) => e.department_id === did && e.active).length;

  const inputCls = "w-full px-3 py-2 rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] focus:border-[var(--gold)] outline-none";

  return (
    <div className="space-y-6" data-testid="departments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading gold-text">{lang === "ar" ? "الأقسام" : "Departments"}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">{items.length} {lang === "ar" ? "قسم" : "departments"}</p>
        </div>
        <button data-testid="add-department-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--gold)] text-white hover:opacity-90">
          <Plus size={16} /> {lang === "ar" ? "قسم جديد" : "New Department"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => {
          const manager = users.find((u) => u.user_id === d.manager_user_id);
          return (
            <div key={d.department_id} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-1" style={{ background: d.color || "#7A1F3D" }} />
              <div className="flex items-start justify-between gap-2 mt-2">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white" style={{ background: d.color || "#7A1F3D" }}>
                    <Building2 size={20} />
                  </div>
                  <div>
                    <div className="font-semibold text-[var(--text)]">{lang === "ar" ? d.name_ar : d.name_en}</div>
                    <div className="text-xs text-[var(--text-muted)] mt-0.5">{d.description || "—"}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => edit(d)} className="p-1.5 rounded hover:bg-[var(--gold)]/10 text-[var(--gold)]"><Pencil size={14} /></button>
                  <button data-testid={`delete-dept-${d.department_id}`} onClick={() => remove(d.department_id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[var(--border)] flex items-center justify-between text-sm">
                <div className="flex items-center gap-1.5 text-[var(--text-muted)]">
                  <UsersIcon size={14} />
                  <span>{empCountBy(d.department_id)} {lang === "ar" ? "موظف" : "employees"}</span>
                </div>
                {manager && <span className="text-xs text-[var(--gold)]">{lang === "ar" ? "المدير:" : "Mgr:"} {manager.name}</span>}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="md:col-span-3 text-center text-[var(--text-muted)] py-16">
            <Building2 className="mx-auto mb-2 opacity-40" size={40} />
            {lang === "ar" ? "لا توجد أقسام. أضف أول قسم." : "No departments. Add your first one."}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="bg-[var(--surface)] rounded-xl p-6 w-full max-w-lg" onMouseDown={onModalPanelMouseDown}>
            <h3 className="text-xl font-heading mb-4">{editId ? (lang === "ar" ? "تعديل قسم" : "Edit") : (lang === "ar" ? "قسم جديد" : "New Department")}</h3>
            <div className="space-y-3">
              <input data-testid="dept-name-en" placeholder="Name (EN)" className={inputCls} value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
              <input data-testid="dept-name-ar" placeholder="الاسم (AR)" className={inputCls} value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
              <textarea placeholder={lang === "ar" ? "الوصف" : "Description"} rows={2} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <select className={inputCls} value={form.manager_user_id} onChange={(e) => setForm({ ...form, manager_user_id: e.target.value })}>
                  <option value="">{lang === "ar" ? "بدون مدير" : "No manager"}</option>
                  {users.filter((u) => ["manager", "admin"].includes(u.role)).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-[var(--text-muted)]">{lang === "ar" ? "اللون:" : "Color:"}</label>
                  <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-14 h-10 rounded-md cursor-pointer" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> {lang === "ar" ? "نشط" : "Active"}</label>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setOpen(false)} className="flex-1 px-4 py-2 border rounded-md">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
              <button data-testid="save-dept-btn" onClick={save} className="flex-1 px-4 py-2 bg-[var(--gold)] text-white rounded-md">{lang === "ar" ? "حفظ" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
